import { useState, type ReactNode } from "react";
import { SpentRow, UsageNotice } from "../plan/usage-notice";
import {
  startPlanRoad,
  type KnockGate,
  type PlanState,
  type Receipt as ReceiptData,
  type Standing,
  type TrialOffer,
} from "../../lib/api";
import {
  AttachButton,
  AttachmentBubble,
  DropOverlay,
  PendingChip,
  useFileDrop,
} from "./attachment";
import {
  CHAT_FIELD,
  ChatLine,
  ChatShell,
  Composer,
  DoorHeader,
  DoormanBubble,
  NobBubbleFace,
  SystemRow,
  ThreadCard,
  type ThreadLine,
} from "./chat";
import { runPositions, type RunItem } from "../../lib/runs";
import { PersonFace } from "../shell/mascot";

/**
 * Who spoke a line, for the runs (wave 90). The knocker is the reader of this
 * thread; a work line and the room's notices belong to nobody.
 */
function runItemOf(line: ThreadLine): RunItem {
  const at = line.at ?? null;
  if (line.work) return { party: null, at };
  if (line.attachment || line.role === "sender") return { party: "you", at };
  if (line.role === "doorman") return { party: "nob", at };
  if (line.role === "recipient") return { party: "them", at };
  return { party: null, at };
}
import { BubbleMenu, COPIED_COPY as COPIED, QuoteBar } from "./bubble-menu";
import { useMessageActions } from "./use-message-actions";
import { useVoiceRecorder } from "./voice";
import { VoiceButton, VoicePreview } from "./voice-button";
import { PLAN_SPENT_LEAD, PLAN_START_LEAD } from "../plan/plan-card";
import { StandingCard } from "./standing-card";
import { TurnInput } from "./turn-input";
import { BudgetBar, WorkIndicator } from "./work-indicator";
import { Receipt } from "./receipt";

export type { ThreadLine };

/** What the thread says when a message's text has been put on the clipboard. */
export { COPIED_COPY } from "./bubble-menu";

/**
 * The caret that rides the end of a sentence being spoken. A block, not a
 * spinner and not an emoji: the doorman is mid-word, and that is all it says.
 */
export const CARET = "▍";

export const CONSIDERING_COPY = "Nob is considering.";

/**
 * THE ROOM CHANGES SHAPE WHEN THE DOOR OPENS (ruling R-0910-7 rule 2).
 *
 * Everything below this line is the sender's half of the shape R-0825-5 gave
 * the recipient: the hearing folds away above, the verdict on it retires, and
 * what is left on screen is two people talking. The doorman's questions are how
 * this person got in, which is backstory the moment they are in.
 */
export const HEARING_FOLD = "What the doorman heard";

/** Where the fold remembers itself — per conversation, so one room's answer is not another's. */
function foldKey(knockId: string): string {
  return `nob.hearing-fold.${knockId}`;
}

/**
 * OPEN ON THE FIRST VISIT AFTER LET-IN, SHUT AFTER (R-0825-5, R-0910-7 rule 2).
 *
 * The first read is the one where the hearing still matters: the sender wants
 * to see what was said about them just before somebody decided. Every visit
 * after that is a conversation, and the record is one press away.
 *
 * The mark is written as the fold is decided rather than when it is closed, so
 * a sender who reloads twice without touching it still gets one open. Every
 * access is wrapped: a browser refusing storage gets the fold open, which is
 * the harmless answer.
 */
function firstVisit(knockId: string): boolean {
  try {
    const key = foldKey(knockId);
    if (window.localStorage.getItem(key) !== null) return false;
    window.localStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}

/**
 * The hearing, folded. A native `<details>` — the same control the recipient's
 * side uses, for the same reasons: it opens on a tap or on Enter, it tells a
 * screen reader what it is with no aria of its own, and its contents stay in
 * the document, so find-in-page and every test that reads the thread can still
 * see the hearing.
 */
function HearingFold({ knockId, children }: { knockId: string; children: ReactNode }) {
  const [open, setOpen] = useState(() => (knockId ? firstVisit(knockId) : true));
  return (
    <details
      data-fold="hearing"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-2xl border border-dashed border-border"
    >
      <summary
        className="cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-[0.1em]
                   text-fainter hover:text-muted focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70"
      >
        {HEARING_FOLD}
      </summary>
      <div className="flex flex-col gap-3 px-3 pb-3">{children}</div>
    </details>
  );
}

/**
 * Where the hearing stops and the conversation begins: the first thing the
 * person behind the door said. `-1` when they have not spoken yet — a door that
 * opened on a mutual knock has a notice and no reply — and then there is
 * nothing under the fold to separate, so the whole thread is the hearing.
 */
export function hearingFoldsAt(lines: ThreadLine[]): number {
  const at = lines.findIndex((line) => line.role === "recipient");
  return at > 0 ? at : -1;
}

/**
 * The fallback headline for a published read whose standing could not be read
 * back. It says what happened — the doorman made its read — and nothing about
 * being finished, because nothing is (`docs/conversation-model.md` §5.2).
 *
 * NO POSSESSIVE (wave 75). The person reading this is the KNOCKER, and the
 * doorman that heard them is the door owner's, not theirs: "Your Nob" told
 * them the read was made by somebody working for them.
 */
export const HEARD_ENOUGH_COPY = "Nob has made its read.";
export const STANDING_COPY = "The standing is not back yet. You can keep talking.";
export const BACK_TO_DOOR_COPY = "Back to the door";

/**
 * How far in the sender is, under the door's name. `null` when nobody knows.
 *
 * IT IS THE INTERROGATION'S METER AND NOTHING ELSE (§6.1). `max_turns` is the
 * doorman's budget for reaching a read, not the conversation's length — so once
 * a read is published the counting stops rather than reporting a number that
 * has stopped meaning anything. The old "concluded"/"over" labels are gone with
 * the states they named: nothing here ends.
 */
export function turnMeta(turn: number, assessed = false): string | null {
  if (assessed) return null;
  return turn > 0 ? `turn ${turn}` : null;
}

type Props = {
  handle: string;
  /** The door's name, carried in from the intake so the header does not change. */
  doorName?: string | null;
  /**
   * The door owner's picture, for the face beside their bubbles once they
   * speak (wave 90). Null draws their initials.
   */
  doorAvatar?: string | null;
  /** The turn the sender is on, or `0` while that is not yet known. */
  turn: number;
  of: number;
  lines: ThreadLine[];
  /** Tokens arriving now — the doorman's line, still being said. */
  streaming: string | null;
  /** Nob has the message and has not started answering. */
  considering: boolean;
  /**
   * WHERE THE SENDER STANDS, and it is the recipient's fact (§3). It decides
   * who the composer talks to: the doorman while `at_door` or `declined`, the
   * PERSON once `let_in`, and nobody once `blocked`.
   */
  gate: KnockGate;
  /**
   * The doorman has published a read (§6.1). It stops the turn meter and puts
   * the standing card in the thread — and it changes nothing else: the composer
   * is still there, and the sender may still talk.
   */
  assessed: boolean;
  /** The guard stopped the doorman listening (§8). No composer, no consolation. */
  guardClosed: boolean;
  /** A refusal or a stumble, in Nob's words, at the foot of the thread. */
  error: string | null;
  draft: string;
  onDraft: (value: string) => void;
  /**
   * Send, handed the message rather than reading the draft back.
   *
   * It takes an argument because of quote-reply: what goes on the wire is the
   * quotation plus what was typed, and that string is composed HERE, at the
   * moment of sending. Handing it over is the only way the caller sends the
   * same text it is about to see in the thread — setting the draft and then
   * calling a no-argument send would send the state as it was one render ago.
   */
  onSend: (message: string) => void;
  /** A turn is in flight. */
  busy: boolean;
  onLeave: () => void;
  /** The budget ratio and whether to show the bar at all (R6). `null` shows nothing. */
  budget?: { ratio: number; show: boolean } | null;
  /**
   * The period is out of room — a paused hearing, a refused turn, a held read,
   * or the low moment (owner decision 2026-08-03). The doorman's own notice
   * arrives in `lines`; this is the one line and one control under it.
   */
  outOfRoom?: boolean;
  /**
   * The sender's own plan: the usage notice at the top of the hearing (ruling
   * R-0921-2 (9)), and the line and control label of the out-of-room row.
   * Handed in rather than read here, because this component renders on a
   * public route that has no query client.
   */
  usagePlan?: PlanState | null;
  /** Open the plan sheet at its options. Absent means there is nothing to press. */
  onSeePlans?: (() => void) | undefined;
  /** The sender's trial offer, when `GET /plan` reports one (ruling R-1006-2). */
  trialOffer?: TrialOffer | null;
  /** Open the plan room at the trial's consent. Absent means the row offers no trial. */
  onStartTrial?: (() => void) | undefined;
  /** The file being sent right now, if there is one. */
  pendingAttachment?: { filename: string; size: number } | null;
  /** A refusal from the upload, said as the room rather than as a bubble. */
  attachmentError?: string | null;
  onAttach?: (file: File) => void;
  onDismissAttachment?: () => void;
  /**
   * A recorded voice note, on its way (owner decision 2026-08-22). Handed the
   * clip rather than a File so the caller decides what to call it — the API
   * judges a recording by its extension, and the recorder is what knows which
   * one it wrote.
   */
  onVoiceNote?: (clip: { blob: Blob; filename: string; durationMs: number }) => void;
  /** The itemised receipt, once the hearing is over and it has been read back. */
  receipt?: ReceiptData | null;
  /** The scored knock's standing (M12) — the card the thread concludes with. */
  standing?: Standing | null;
  /** The knock's own id, so a settled receipt can be shared from here too (§1.8 loop 3). */
  knockId?: string;
  /** The shell owns the header and the height — see `ChatShell`'s `fill`. */
  embedded?: boolean;
  /**
   * THE DOOR OPENED AND THERE IS SOMEBODY TO TALK TO (owner, 2026-08-26).
   *
   * Present once the gate is `let_in` — the recipient's own decision, on the
   * sender's own view (§6.1) — or once a `recipient` line has landed, which is
   * the same fact arriving by the older road. When it is here, the composer at
   * the foot addresses the PERSON rather than the doorman, and it is a
   * different composer on purpose: no turn counter, no attachment, no
   * microphone, no budget bar. Those all belong to the interrogation, and the
   * doorman has stepped aside permanently (§3).
   */
  personTalk?: {
    /** Whose thread this is now — the door's name, on the send button and field. */
    name: string;
    draft: string;
    onDraft: (value: string) => void;
    onSend: () => void;
    busy: boolean;
    /** Said as the room, at the foot, when a message did not go. */
    error?: string | null;
  } | null;
  /** Anything the surface wants above the first line of the transcript. */
  above?: ReactNode;
  /**
   * THE AI DISCLOSURE (EU AI Act Art. 50; ruling R-0914-1 (11)). Server-authored
   * and rendered above everything else in the column, including `above`: it says
   * what this conversation IS, and a sentence that appears after the first thing
   * the doorman said has been read too late to be a disclosure. Empty draws
   * nothing.
   */
  disclosure?: string;
};

/**
 * The grilling itself (M9, FR-3.3) — the same conversation the intake was asked
 * in, continuing. One column, three voices: the doorman on the left, the
 * sender's own words on the right in the door's amber, and the room's notices
 * centred between them. The standing lands as the last card in it rather than
 * replacing it, so the hearing and its resolution read as one thing (M12).
 *
 * Free of network state, so every decision that matters (who is speaking,
 * whether the sender may speak, what a finished conversation says) is a function
 * of these props alone and can be asserted directly.
 */
export function KnockThread({
  handle,
  doorName = null,
  doorAvatar = null,
  turn,
  lines,
  streaming,
  considering,
  disclosure = "",
  gate,
  assessed,
  guardClosed,
  error,
  draft,
  onDraft,
  onSend,
  busy,
  onLeave,
  budget = null,
  outOfRoom = false,
  usagePlan = null,
  onSeePlans,
  trialOffer = null,
  onStartTrial,
  pendingAttachment = null,
  attachmentError = null,
  onAttach,
  onDismissAttachment,
  onVoiceNote,
  receipt = null,
  standing = null,
  knockId,
  embedded = false,
  personTalk = null,
  above = null,
}: Props) {
  /**
   * CAN THE SENDER STILL SPEAK TO THE DOORMAN (§6.1)? Two things stop them and
   * neither is a verdict: being blocked, and the guard having stopped the
   * doorman listening. A published read stops nothing — the composer is present
   * in every other state, and a knock the recipient declined keeps it, because
   * the gatekeeper is still standing there (§5.2).
   *
   * `let_in` is the third case and is not a refusal either: the doorman has
   * stepped aside and the composer becomes the write-back lane, which is
   * `personTalk` below rather than this.
   */
  const canSpeak = (gate === "at_door" || gate === "declined") && !guardClosed;
  /** The out-of-room row is up — and while it is, the notice at the top is not. */
  const outOfRoomRow = outOfRoom && canSpeak;
  /**
   * A document is evidence for the doorman (§6.3, E-27), so it goes up only
   * while there is a doorman listening and somewhere to send it.
   */
  const canAttach = canSpeak && Boolean(onAttach);

  const drop = useFileDrop({ onFile: onAttach ?? (() => {}), enabled: canAttach });

  /**
   * The menu and the quote. `onMenu` is handed to landed bubbles only — a
   * streaming line is a sentence still being written, and quoting half of one
   * would put half a sentence on the wire.
   */
  const composerField = () => document.getElementById("nob-turn") as HTMLTextAreaElement | null;
  const actions = useMessageActions(() => composerField()?.focus());

  /** A quote rides with the message, and is spent by being sent. */
  const send = () => onSend(actions.compose(draft));

  /**
   * THE DOOR IS OPEN (ruling R-0910-7 rule 2) — the recipient's own decision, on
   * the sender's own view (§3), and the only thing that changes this room's
   * shape. A `recipient` line arriving against a gate still at the door is not
   * it: that is the half-opened knock of 2026-09-09, and the fold must follow
   * the fact rather than the symptom.
   */
  const letIn = gate === "let_in";
  const foldAt = letIn ? hearingFoldsAt(lines) : -1;

  /**
   * FACES BESIDE BUBBLES (wave 90). The reader here is the knocker: their own
   * lines and files are never faced; Nob's runs wear Nob's face and the door
   * owner's runs wear theirs, on the last bubble of each run, with the owner's
   * name above the first. A work line and the room's notices break a run. The
   * streaming line is Nob's and joins the run it follows.
   */
  const personName = personTalk?.name ?? doorName ?? handle;
  const runs = runPositions([
    ...lines.map(runItemOf),
    ...(streaming !== null ? [{ party: "nob", at: null }] : []),
  ]);
  const gutterFor = (line: ThreadLine, i: number): ReactNode | null | undefined => {
    const party = runItemOf(line).party;
    if (party !== "nob" && party !== "them") return undefined;
    if (!runs[i]?.last) return null;
    return party === "nob" ? (
      <NobBubbleFace />
    ) : (
      <PersonFace name={personName} handle={handle} avatar={doorAvatar} size="bubble" />
    );
  };

  const renderLine = (line: ThreadLine, i: number) =>
    line.work ? (
      // The doorman's work, where it happened (wave 82) — under the line that
      // asked for it, live and on a reload alike. Work, never money.
      <WorkIndicator key={`work-${line.work.id}`} items={[line.work]} />
    ) : line.attachment ? (
      // A document is a sender's line like any other — it renders where it
      // was said, not appended after whatever came later (§6.3).
      <AttachmentBubble
        key={`${i}-attachment`}
        attachment={line.attachment}
        onMenu={actions.open}
      />
    ) : (
      <ChatLine
        key={`${i}-${line.role}`}
        line={line}
        onMenu={actions.open}
        // A `recipient` line is the person behind the door speaking, and it
        // has to say who that is. The door's own name is who — once, above
        // the first bubble of their run.
        personName={personName}
        gutter={gutterFor(line, i)}
        label={runs[i]?.first ?? true}
      />
    );

  /**
   * The recorder is held here rather than inside the mic, because two parts of
   * the composer read it: the button that starts and stops, and the preview
   * above the field that plays the clip back before it goes anywhere.
   */
  const voice = useVoiceRecorder();

  return (
    <ChatShell
      fill={embedded}
      dropHandlers={drop.handlers}
      overlay={drop.dragging ? <DropOverlay /> : null}
      header={
        embedded ? (
          budget?.show ? (
            <BudgetBar ratio={budget.ratio} />
          ) : null
        ) : (
          <DoorHeader
            name={doorName}
            handle={handle}
            meta={turnMeta(turn, assessed)}
            onBack={onLeave}
            backLabel={BACK_TO_DOOR_COPY}
            // Reassurance under the header, and only when the server asks for it.
            bar={budget?.show ? <BudgetBar ratio={budget.ratio} /> : null}
          />
        )
      }
      composer={
        personTalk ? (
          /* The door opened and there is somebody to talk to. One field and a
             send — and no `turn` prop, because there are no turns any more.
             FIRST, ahead of the doorman's composer (§3, §9): letting in ends the
             gatekeeper phase where it stands, mutual knocks included, and a
             turn on a let-in knock is refused by the server anyway. */
          <PersonComposer talk={personTalk} />
        ) : canSpeak ? (
          <TurnInput
            value={draft}
            onChange={onDraft}
            onSend={send}
            disabled={busy}
            turn={turn}
            // After the read the box said "Answer the question." under a
            // closing line, with nothing asked (R-0925-1 (1)). What the knocker
            // may do then is add to what the doorman has — unless the doorman
            // has since asked something (a question the owner relayed), which
            // is answered like any other. Before the read the field keeps
            // asking for an answer.
            {...(assessed && !questionPending(lines)
              ? { placeholder: addSomethingFor(doorName?.trim() || `@${handle}`) }
              : {})}
            attach={
              canAttach ? <AttachButton onFile={onAttach ?? (() => {})} disabled={busy} /> : null
            }
            // The mic sits on the far side of the field, where the prototype
            // puts it, and only where there is somewhere to send a clip.
            voice={canAttach && onVoiceNote ? <VoiceButton voice={voice} disabled={busy} /> : null}
            quote={
              actions.quote ? <QuoteBar quote={actions.quote} onClear={actions.clearQuote} /> : null
            }
            onPasteFiles={
              canAttach && onAttach
                ? (files) => {
                    const first = files[0];
                    if (first) onAttach(first);
                  }
                : undefined
            }
            chip={
              <>
                {/* The clip, played back before it is sent — and, while one is
                    being made, the counter that says so. */}
                {canAttach && onVoiceNote && <VoicePreview voice={voice} onSend={onVoiceNote} />}
                {pendingAttachment ? (
                  <PendingChip
                    filename={pendingAttachment.filename}
                    size={pendingAttachment.size}
                    uploading
                    onDismiss={onDismissAttachment ?? (() => {})}
                  />
                ) : null}
              </>
            }
          />
        ) : null
      }
    >
      {disclosure ? (
        <p
          data-testid="ai-disclosure"
          className="mb-3 text-center text-[11px] leading-relaxed text-fainter"
        >
          {disclosure}
        </p>
      ) : null}
      {/* THE NOTICE, AT THE TOP OF AN OPEN KNOCK (ruling R-0921-2 (9)). The
          second of its two places, and the one that matters most to a sender:
          they are about to ask the doorman for more work, and the honest moment
          to say the month is nearly used is before they do. It draws nothing
          unless the server says there is a notice, and nothing while the
          out-of-room row below carries the same line and control. */}
      {!outOfRoomRow && (
        <UsageNotice plan={usagePlan ?? null} where="knock" onAction={onSeePlans} />
      )}
      {above}
      {/* THE HEARING FOLDS AWAY ONCE THE DOOR IS OPEN (ruling R-0910-7 rule 2).
          Before that nothing changes: a knock still at the door IS the hearing,
          and folding it would hide the whole screen. The split is the first
          thing the person said, exactly as it is on the recipient's side. */}
      {foldAt > 0 ? (
        <>
          <HearingFold knockId={knockId ?? ""}>
            {lines.slice(0, foldAt).map(renderLine)}
          </HearingFold>
          {lines.slice(foldAt).map((line, i) => renderLine(line, i + foldAt))}
        </>
      ) : (
        lines.map(renderLine)
      )}

      {/* Stable region: what the doorman is saying is announced as it lands. */}
      <div aria-live="polite" className="contents">
        {streaming !== null && (
          <DoormanBubble gutter={<NobBubbleFace />}>
            {/* Trailing whitespace is the model's, before its trailer, and
                drew an empty band under the words (wave 82) — the committed
                line is trimmed too (`started.tsx#commit`). */}
            {streaming.trimEnd()}
            <span className="nob-breathe ml-px text-accent-ink/90">{CARET}</span>
          </DoormanBubble>
        )}

        {considering && streaming === null && <SystemRow breathe>{CONSIDERING_COPY}</SystemRow>}
      </div>

      {/* An upload that will not land is the room's line, not the sender's
          bubble: nothing was said, so nothing is quoted back. */}
      <div aria-live="polite" className="contents">
        {attachmentError && <SystemRow>{attachmentError}</SystemRow>}
      </div>

      {/* THE WEAK CLOSE IS GONE, and so is the control under it (§5.1, §10). It
          told a sender to strengthen their case or conclude; a low read is now
          published like any other, the sender sees it and may keep talking, and
          that is the same thing said honestly. */}

      {/* THE OUT-OF-ROOM MOMENT: one line and one control, blocking nothing
          (owner, 2026-09-23; wave 57). It was the whole plan card — the rung
          above priced, "Reset for $20" and a "Not now" beside it. The line is
          the notice's own, the server's, so it says "approaching" at the low
          moment and "used" at the wall; the control opens the plan sheet at
          its options, where the prices and the consent are. The notice at the
          top stands down while this row is up: one control on the screen. */}
      {outOfRoomRow && (
        <div data-out-of-room>
          <SpentRow
            line={
              usagePlan?.usage_notice_line ??
              (usagePlan && startPlanRoad(usagePlan) ? PLAN_START_LEAD : PLAN_SPENT_LEAD)
            }
            plan={usagePlan}
            onAction={onSeePlans}
            trial={trialOffer}
            onStartTrial={onStartTrial}
          />
        </div>
      )}

      {/* A refusal or a stumble: the doorman's own voice, at the foot of what it
          was answering — and above the composer holding the words it lost. */}
      {error && (
        <DoormanBubble alert gutter={<NobBubbleFace />}>
          {error}
        </DoormanBubble>
      )}

      {/* THE READ, PUBLISHED (M12) — a card IN the conversation, not the end of
          one. The composer is still below it, and a turn that changes the
          doorman's mind replaces this card with the new standing (§5.2).

          IT RETIRES WHEN THE DOOR OPENS (ruling R-0910-7 rule 2). "A reasonable
          knock — too few knocks here yet to rank", with "What would move you
          up" under it, is a verdict on whether this person should get in; it is
          moot once they are in and talking, and leaving it at the foot of a
          conversation makes the room read as a hearing that somebody replied
          to. It is not deleted — a knock still at the door still gets it. */}
      {!letIn && assessed && standing && (
        <StandingCard handle={handle} standing={standing} knockId={knockId} doorName={doorName} />
      )}

      {/* Nob does not thank anyone for their time. The fallback for a read whose
          standing could not be fetched back.

          NEVER OVER A HELD READ (wave 75). A result the doorman decided across
          the line (ruling R-0921-2 (6)) is the out-of-room row's to say, and
          the row alone: "the standing is not back yet" under it said the read
          was late when it is waiting on room. And no way out of the card — it
          sent the sender "Back to the door", which on a desk was a button that
          did nothing and on a phone repeated the header's own back. */}
      {!letIn && assessed && !standing && !outOfRoom && (
        <ThreadCard>
          <p className="text-[15px] leading-relaxed text-text" role="status">
            {HEARD_ENOUGH_COPY}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-faint">{STANDING_COPY}</p>

          {/* What the doorman did, beneath the headline (FR-3.5). No figure on
              it: this is the thread the grilling happened in, and the
              accounting is the plan's share of its period, on the plan screen
              (ruling R-0825-3). */}
          {receipt && <Receipt receipt={receipt} knockId={knockId} />}
        </ThreadCard>
      )}

      {/* A KNOCK THE GUARD ENDED SAYS SO IN ITS OWN WORDS AND NOWHERE ELSE
          (§8). The guard writes its line into the transcript as a notice, so it
          is already above; a second sentence invented here would be the client
          narrating a stop it did not make. What the guard leaves behind is the
          absence of a composer, which `canSpeak` is. */}

      {/* A message to a person that did not go. Said as the room, at the foot,
          exactly where a refusal from the doorman would be. */}
      {personTalk?.error && <SystemRow>{personTalk.error}</SystemRow>}

      {/* Copy is silent to look at and announced to listen to: a toast for
          something that took no decision would be a toast nobody asked for. */}
      <span className="sr-only" role="status" aria-live="polite">
        {actions.copied ? COPIED : ""}
      </span>

      {actions.menu && (
        <BubbleMenu
          target={actions.menu}
          onCopy={actions.copy}
          onQuote={actions.startQuote}
          onClose={actions.close}
        />
      )}
    </ChatShell>
  );
}

/** What the doorman's field says once the read has gone to the owner (R-0925-1 (1)). */
export function addSomethingFor(name: string): string {
  return `Add something for ${name}’s doorman`;
}

/**
 * Is a question waiting on the knocker? The last thing said in the thread is
 * the doorman's, and it asks. Work lines and files are not things said.
 */
export function questionPending(lines: readonly ThreadLine[]): boolean {
  const last = [...lines].reverse().find((l) => !l.work && !l.attachment);
  return last?.role === "doorman" && last.content.trim().endsWith("?");
}

/** What the field says when the person behind the door has answered. */
export function personPlaceholder(name: string): string {
  return `Message ${name}`;
}

/**
 * The composer for a conversation between two people — deliberately the plain
 * one.
 *
 * `TurnInput` carries the turn counter, the paperclip and the microphone,
 * because every one of those is a fact about the hearing: which turn this is,
 * that a document may still be entered as evidence, that a clip will be
 * transcribed and scored. None of them mean anything once the door is open, and
 * a field that still offered them would be promising that the doorman is still
 * listening. It is not. This is a message to a person.
 */
function PersonComposer({
  talk,
}: {
  talk: {
    name: string;
    draft: string;
    onDraft: (v: string) => void;
    onSend: () => void;
    busy: boolean;
  };
}) {
  return (
    <Composer
      onSubmit={talk.onSend}
      disabled={talk.busy || talk.draft.trim() === ""}
      sendLabel={`Send to ${talk.name}`}
    >
      <label htmlFor="nob-person-message" className="sr-only">
        {personPlaceholder(talk.name)}
      </label>
      <textarea
        id="nob-person-message"
        value={talk.draft}
        onChange={(e) => talk.onDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            talk.onSend();
          }
        }}
        rows={1}
        placeholder={personPlaceholder(talk.name)}
        className={`${CHAT_FIELD} max-h-40 resize-none`}
      />
    </Composer>
  );
}

/**
 * A transcript, read-only, in the same three voices as the live grilling. The
 * door's own plate renders its prose this way, so what a sender reads before
 * the hearing and what they read inside it are one surface.
 */
export function Transcript({
  lines,
  personName,
}: {
  lines: ThreadLine[];
  /** Who a `recipient` line is — the door's name, on a read-only transcript. */
  personName?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      {lines.map((line, i) => (
        <ChatLine key={`${i}-${line.role}`} line={line} {...(personName ? { personName } : {})} />
      ))}
    </div>
  );
}
